import { EventEmitter, Injectable } from '@angular/core';
import { Ticket } from '@ridango/core';
import Env from '@ridango/core/env';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActionSheetController, LoadingController, ModalController, PopoverController } from '@ionic/angular';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import {
    AccountTicketsNgrxFacade as TicketsFacade,
    FavoritesFacade,
    PurchaseHistoryFacade,
    TicketsService,
    NotificationFacade,
    SessionService,
    ShoppingCartService
} from '@ridango/components';
import { EMPTY, Observable } from 'rxjs';
import { EnhancedFareEngineTicket } from '@ridango/components/lib/account-tickets-ngrx/models/ticket.model';
import { TranslateService } from '@ngx-translate/core';
import { activateManually, openTicketActionModal } from './ticket-action.utils';
import { TICKET_ACTIONS } from './ticket-actions.catalog';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { RemoveExpiredTicketModalComponent } from '../../containers/remove-expired-ticket-modal/remove-expired-ticket-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { DeviceIdService } from '@ridango/core/lib/services';
import { AnonymousUserModalToShoppingCartComponent } from '../../containers/anonymous-user-modals/anonymous-user-modal-to-shopping-cart/anonymous-user-modal-to-shopping-cart.component';

export type TicketData = Ticket & EnhancedFareEngineTicket;

export interface TicketCtx {
    ticket: TicketData;
    env: Env;
    isHistory: boolean;
    isAuth: boolean;
    locale: string;
    isFavourite?: boolean;
    refresh?: EventEmitter<void>;
    goBack?: () => void;
    startDateStr?: string;
    endDateStr?: string;
    ticketTitle$?: Observable<string>;
    isTopUp?: boolean;
}

export interface ActionDI {
    modal: ModalController;
    popover: PopoverController;
    actionSheet: ActionSheetController;
    fb: FormBuilder;
    ticketSvc: TicketsService;
    ticketsFacade: TicketsFacade;
    favoritesFacade: FavoritesFacade;
    historyFacade: PurchaseHistoryFacade;
    loadingCtrl: LoadingController;
    translate: TranslateService;
    buyTicketAgain: (ctx: TicketCtx) => void;
    removeExpiredTicket: (ctx: TicketCtx) => void;
    openTicketModal: (action: string, form: FormGroup, ctx: TicketCtx) => Promise<void>;
    activateTicketModal: (ctx: TicketCtx, callback?: () => void) => Promise<void>;
    sendTicketAnalyticEvent: (name: string) => void;
}

export interface TicketActionDef {
    id: string;
    labelKey: string;
    cssClass?: {
        onSheet?: string;
        onDetails?: string;
    };
    dynamicLabel?: (ctx: TicketCtx) => string;
    show: (ctx: TicketCtx) => boolean;
    disable?: (ctx: TicketCtx) => boolean;
    run: (ctx: TicketCtx, di: ActionDI) => Promise<void> | void;
}

export interface UiAction {
    id: string;
    text: string;
    disabled: boolean;
    cssClass?: string;
    handler?: () => void;
}

@Injectable()
export class TicketActionsService {
    private di: ActionDI;

    constructor(
        translate: TranslateService,
        modal: ModalController,
        popover: PopoverController,
        actionSheet: ActionSheetController,
        fb: FormBuilder,
        private analytics: AnalyticsService,
        private matDialog: MatDialog,
        private notification: NotificationFacade,
        private deviceIdService: DeviceIdService,
        private sessionService: SessionService,
        private shoppingCartService: ShoppingCartService,
        ticketSvc: TicketsService,
        ticketsFacade: TicketsFacade,
        favoritesFacade: FavoritesFacade,
        historyFacade: PurchaseHistoryFacade,
        loadingCtrl: LoadingController
    ) {
        this.di = {
            modal,
            popover,
            actionSheet,
            fb,
            ticketSvc,
            ticketsFacade,
            favoritesFacade,
            historyFacade,
            loadingCtrl,
            translate,
            buyTicketAgain: (ctx: TicketCtx) => this.buyTicketAgain(ctx),
            removeExpiredTicket: (ctx: TicketCtx) => this.removeExpiredTicket(ctx),
            openTicketModal: (action: string, form: FormGroup, ctx: TicketCtx) =>
                openTicketActionModal(action, form, ctx, this.di),
            activateTicketModal: (ctx: TicketCtx, callback) => activateManually(ctx, this.di, callback),
            sendTicketAnalyticEvent: (name: string) => this.sendTicketAnalyticEvent(name)
        };
    }

    public createActionsList(ctx: TicketCtx, actionName: string, withStyles?: 'onSheet' | 'onDetails'): UiAction[] {
        return TICKET_ACTIONS.filter((a) => a.show(ctx)).map((a) => {
            const transKey = a.dynamicLabel ? a.dynamicLabel(ctx) : a.labelKey;
            const txt = this.di.translate.instant(transKey);

            return {
                id: a.id,
                text: txt,
                disabled: a.disable?.(ctx) ?? false,
                cssClass: withStyles ? a.cssClass[withStyles] : '',
                handler: () => {
                    this.analyticTrack(a.labelKey, actionName);
                    a.run(ctx, this.di);
                }
            };
        });
    }

    private removeExpiredTicket(ctx: TicketCtx): void {
        const removeTicketDialog = this.matDialog.open(RemoveExpiredTicketModalComponent, {
            width: '80%',
            maxWidth: '80vh',
            panelClass: 'remove-ticket-dialog-wrapper'
        });

        removeTicketDialog
            .afterClosed()
            .pipe(
                take(1),
                filter((confirmed: boolean) => confirmed),
                switchMap(() =>
                    this.di.ticketSvc.removeHistoryTicket(ctx.ticket.docNum).pipe(
                        catchError((error) => {
                            console.error(error);
                            this.notification.errorV2(this.di.translate.instant('EXPIRED_TICKET_REMOVE_FAILED'));
                            return EMPTY;
                        })
                    )
                )
            )
            .subscribe(() => {
                this.notification.success(this.di.translate.instant('EXPIRED_TICKET_REMOVE_SUCCESSFUL'));
                ctx.refresh?.emit();
                if (ctx?.goBack) {
                    ctx.goBack();
                }
            });
    }

    private buyTicketAgain(ctx: TicketCtx) {
        this.deviceIdService
            .getIsUserExist()
            .pipe(take(1))
            .subscribe((deviceUserExist) => {
                const routeParams: string[] = ['purchase-v2', 'cart'];

                this.shoppingCartService.startToBuyTicket(
                    deviceUserExist,
                    routeParams,
                    AnonymousUserModalToShoppingCartComponent,
                    ctx.ticket,
                    this.sessionService.getLoggedInEmail()
                );
            });
    }

    private analyticTrack(key: string, actionName: string) {
        const dictionary = this.di.translate.store.translations['en'];
        if (!dictionary) {
            return;
        }

        this.sendTicketAnalyticEvent(`${dictionary[key]} - ${actionName}`);
    }

    private sendTicketAnalyticEvent(name: string) {
        this.analytics.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name
        });
    }
}
